/**
 * Admin Rituals Tab
 * Displays community rituals for admins with AdminRitualCard
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useCommunityRitualRequests } from "@/features/rituals/hooks/useCommunityRitualRequests"
import { useCommunityRituals } from "@/features/rituals/hooks/useCommunityRituals"
import { formatRitualType } from "@/features/rituals/types"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { useCommunityStatistics } from "../hooks/useCommunity"
import type { CommunityDetail } from "../types/community-detail"
import { AdminRitualCard } from "./AdminRitualCard"
import { CommunityRequestCard } from "./CommunityRequestCard"

interface AdminRitualsTabProps {
  communityId: string
  searchQuery?: string
  isJoined?: boolean
  isPending?: boolean
  permissions?: CommunityDetail["permissions"]
}

export function AdminRitualsTab({
  communityId,
  searchQuery = "",
  isJoined,
  isPending,
  permissions,
}: AdminRitualsTabProps) {
  const { t } = useTranslation("communities")
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("all")

  const debouncedSearch = useDebounce(searchQuery, 500)

  // Fetch rituals for all/archived tabs
  const {
    data,
    isLoading: isRitualsLoading,
    isError: isRitualsError,
  } = useCommunityRituals(communityId, {
    limit: 50,
    search: debouncedSearch || undefined,
    status:
      activeTab === "archived" ? "archived" : activeTab === "requests" ? "requests" : "joined",
    enabled: activeTab !== "requests",
  })

  // Fetch ritual requests for requests tab
  const {
    data: requestsData,
    isLoading: isRequestsLoading,
    isError: isRequestsError,
  } = useCommunityRitualRequests(communityId, {
    limit: 50,
    search: debouncedSearch || undefined,
    enabled: activeTab === "requests",
  })

  const { data: statistics } = useCommunityStatistics(communityId, {
    enabled: Boolean(isJoined),
  })

  const totalCount = statistics?.ritualsAll ?? data?.total ?? 0
  const archivedCount = statistics?.ritualsArchived ?? 0
  const requestsCount = statistics?.ritualRequests ?? 0

  const rituals =
    data?.rituals.map(ritual => ({
      id: ritual.id,
      image: ritual.uploads?.find(u => u.isCover)?.filePath || undefined,
      title: ritual.title,
      type: formatRitualType(ritual.type),
      price: ritual.price,
      participationFee: ritual.participationFee,
      dateTime: ritual.date ? new Date(ritual.date).toLocaleDateString() : undefined,
      duration: `${ritual.duration}`,
      location: ritual.location || undefined,
      viewsCount: 0,
      joinedCount: ritual.memberCount || 0,
      completedCount: 0,
      earnings: ritual.totalEarnings || 0,
      chatCount: ritual.chatCount || 0,
      isArchived: ritual.isArchived ?? false,
      communityId,
    })) || []

  const requests =
    requestsData?.requests.map(req => ({
      id: req.id,
      organizerRecordId: req.organizerRecordId,
      image: req.coverImage || undefined,
      title: req.title,
      type: formatRitualType(req.type as Parameters<typeof formatRitualType>[0]),
      price: req.price ?? undefined,
      participationFee: req.participationFee,
      dateTime: req.date ? new Date(req.date).toLocaleDateString() : undefined,
      duration: `${req.duration}`,
      location: req.location || undefined,
      ritualCreatorId: req.ritualCreatorId,
      inviterName: req.inviter?.name || "Someone",
      inviterUsername: req.inviter?.username || undefined,
      inviterAvatar: req.inviter?.avatar || undefined,
      inviteMessage: `${req.inviter?.name || "Someone"} is inviting you to be a co creator of this ritual!`,
      inviteNote: req.inviteNote || undefined,
    })) || []

  const isLoading = activeTab === "requests" ? isRequestsLoading : isRitualsLoading
  const isError = activeTab === "requests" ? isRequestsError : isRitualsError

  return (
    <div className="mt-7 mb-10 mx-2">
      {/* Header with tabs and Add Ritual button */}
      <div className="flex flex-col md:flex-row items-center justify-start gap-3 mb-9">
        <h2 className="text-lg font-semibold text-charcoal font-montserrat uppercase">
          {t("rituals.title")}
        </h2>
        <div className="flex flex-col md:flex-row flex-1 justify-between items-center gap-4">
          <div className="flex items-center border-0 md:border-l-1 ms-1">
            <button
              type="button"
              onClick={() => setActiveTab("all")}
              className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === "all"
                  ? "border-primary text-charcoal"
                  : "border-transparent text-body hover:text-charcoal"
              }`}
            >
              {t("rituals.all")} ({totalCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("archived")}
              className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === "archived"
                  ? "border-primary text-charcoal"
                  : "border-transparent text-body"
              }`}
            >
              {t("rituals.archived")} ({archivedCount})
            </button>
            {permissions?.isOwner && (
              <button
                type="button"
                onClick={() => setActiveTab("requests")}
                className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                  activeTab === "requests"
                    ? "border-primary text-charcoal"
                    : "border-transparent text-stone hover:text-charcoal"
                }`}
              >
                {t("rituals.requests")} ({requestsCount})
              </button>
            )}
          </div>
          {isJoined && !isPending && permissions?.canAddRituals && (
            <Button
              variant="primary"
              className="uppercase px-15"
              onClick={() => {
                navigate(`/rituals/create?communityId=${communityId}`, {
                  state: { canAddCommunityAsOrganizer: permissions?.canAddRituals },
                })
              }}
            >
              {t("rituals.addRitual")}
            </Button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">{t("rituals.loading")}</p>
        </div>
      )}

      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">{t("rituals.error")}</p>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="">
          {activeTab === "requests" ? (
            requests.length === 0 ? (
              <div className="h-[180px] py-0">
                <p className="text-lg font-semibold text-charcoal">No pending requests</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {requests.map(request => (
                  <CommunityRequestCard key={request.id} {...request} />
                ))}
              </div>
            )
          ) : rituals.length === 0 ? (
            activeTab === "archived" ? (
              <div className="h-[180px] py-0">
                <p className="text-lg font-semibold text-charcoal">No rituals archived</p>
              </div>
            ) : (
              <div className="h-[360px] py-7 ">
                <p className="text-lg font-semibold text-charcoal mb-0 text-[18px] font-monteserat uppercase">
                  {t("rituals.emptyTitle")}
                </p>
                <p className="text-sm mt-4 text-stone">{t("rituals.emptyDescription")}</p>
                {!isJoined && !isPending && (
                  <Button
                    variant="primary"
                    className="mt-6 px-20 uppercase"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent("community-join-click"))
                    }}
                  >
                    {t("rituals.emptyJoinCommunity")}
                  </Button>
                )}
                {isJoined && !isPending && permissions?.canAddRituals && (
                  <Button
                    variant="primary"
                    className="mt-6 px-20 uppercase"
                    onClick={() => {
                      navigate(`/rituals/create?communityId=${communityId}`, {
                        state: { canAddCommunityAsOrganizer: permissions?.canAddRituals },
                      })
                    }}
                  >
                    {t("rituals.emptyAddRitual")}
                  </Button>
                )}
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rituals.map(ritual => (
                <AdminRitualCard key={ritual.id} {...ritual} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
